import { authClient } from './amplifyClient';
import { getCompanyOwner, listCompanyOwnerByCompanyId, usersByCompanyId } from '../graphql/queries';
import {
    deleteCompanyOwner as deleteCompanyOwnerMutation,
    deleteUser as deleteUserMutation,
    updateCompany as updateCompanyMutation,
} from '../graphql/mutations';
import { User } from '../types/schema';
import { Role, resolveRole } from './permissions';
import { isUnlinkedInvite } from './preferredCompany';

type CompanyOwnerRow = {
    id: string;
    email?: string | null;
    name?: string | null;
    role?: string | null;
    companyId?: string | null;
    cognitoId?: string | null;
    /** Present on CompanyOwner; used by profile save to detect owner-only directory rows. */
    subscriptionType?: string | null;
};

/**
 * Score a directory row so confirmed (linked Cognito) wins over pending invites
 * when the same email appears twice after archive/live company migrations.
 */
export function companyUserDirectoryRank(user: User, companyOwnerId?: string | null): number {
    let score = 0;
    if (companyOwnerId && user.id === companyOwnerId) score += 100;
    if (!isUnlinkedInvite(user.cognitoId)) score += 50;
    if (resolveRole(user.role) === Role.SUPER_ADMIN) score += 10;
    if (user.name?.trim()) score += 1;
    return score;
}

/**
 * Collapse duplicate emails in a company directory.
 * Keeps the strongest row (linked Cognito / owner) and drops stale "Resend Invite" twins.
 */
export function dedupeCompanyUsersByEmail(
    users: User[],
    companyOwnerId?: string | null,
): User[] {
    const byEmail = new Map<string, User>();
    const withoutEmail: User[] = [];

    for (const user of users) {
        const email = user.email?.trim().toLowerCase();
        if (!email) {
            withoutEmail.push(user);
            continue;
        }

        const existing = byEmail.get(email);
        if (!existing) {
            byEmail.set(email, user);
            continue;
        }

        const existingRank = companyUserDirectoryRank(existing, companyOwnerId);
        const nextRank = companyUserDirectoryRank(user, companyOwnerId);
        if (nextRank > existingRank) {
            byEmail.set(email, user);
        } else if (nextRank === existingRank && (user.updatedAt || '') > (existing.updatedAt || '')) {
            byEmail.set(email, user);
        }
    }

    return [...withoutEmail, ...byEmail.values()];
}

/** Load every User row for a company (paginated). */
export async function fetchAllUsersForCompany(companyId: string): Promise<User[]> {
    const allItems: User[] = [];
    let token: string | null | undefined = undefined;

    do {
        const result = (await authClient.graphql({
            query: usersByCompanyId,
            variables: { companyId, limit: 1000, nextToken: token },
        })) as {
            data?: { usersByCompanyId?: { items?: (User | null)[]; nextToken?: string | null } };
        };
        const data = result.data?.usersByCompanyId;
        allItems.push(...(data?.items ?? []).filter((item): item is User => item != null));
        token = data?.nextToken;
    } while (token);

    return allItems;
}

async function fetchCompanyOwnerRecords(
    companyId: string,
    companyOwnerId?: string | null,
): Promise<CompanyOwnerRow[]> {
    const byId = new Map<string, CompanyOwnerRow>();

    if (companyOwnerId) {
        try {
            const result = (await authClient.graphql({
                query: getCompanyOwner,
                variables: { id: companyOwnerId },
            })) as { data?: { getCompanyOwner?: CompanyOwnerRow | null } };
            const owner = result.data?.getCompanyOwner;
            if (owner?.id) {
                byId.set(owner.id, owner);
            }
        } catch {
            // fall through to listCompanyOwnerByCompanyId
        }

        if (!byId.has(companyOwnerId)) {
            try {
                const { data: ownerRecord } = await authClient.models.CompanyOwner.get({ id: companyOwnerId });
                if (ownerRecord?.id) {
                    byId.set(ownerRecord.id, ownerRecord as CompanyOwnerRow);
                }
            } catch {
                // list query below
            }
        }
    }

    let token: string | null | undefined = undefined;
    do {
        const result = (await authClient.graphql({
            query: listCompanyOwnerByCompanyId,
            variables: { companyId, limit: 100, nextToken: token },
        })) as {
            data?: { listCompanyOwnerByCompanyId?: { items?: (CompanyOwnerRow | null)[]; nextToken?: string | null } };
        };
        const data = result.data?.listCompanyOwnerByCompanyId;
        for (const item of data?.items ?? []) {
            if (item?.id) {
                byId.set(item.id, item);
            }
        }
        token = data?.nextToken;
    } while (token);

    return Array.from(byId.values());
}

/**
 * Ensures company owner / super-admin accounts appear in /users even when they
 * only exist on CompanyOwner, and aligns role on duplicate User rows (same email).
 */
export function mergeCompanyOwnersIntoUserDirectory(
    users: User[],
    owners: CompanyOwnerRow[],
    companyOwnerId?: string | null,
): User[] {
    const directory = users.map((u) => ({ ...u }));

    for (const owner of owners) {
        const ownerRole = owner.role || 'owner';
        const ownerEmail = owner.email?.trim().toLowerCase();

        const byIdIdx = directory.findIndex((u) => u.id === owner.id);
        if (byIdIdx >= 0) {
            // Real User row exists for this owner id — keep User as the directory row,
            // but align role to Super Admin for the primary company owner.
            if (owner.id === companyOwnerId && resolveRole(directory[byIdIdx].role) !== Role.SUPER_ADMIN) {
                directory[byIdIdx] = { ...directory[byIdIdx], role: ownerRole };
            }
            continue;
        }

        if (ownerEmail) {
            const byEmailIdx = directory.findIndex(
                (u) => u.email?.trim().toLowerCase() === ownerEmail,
            );
            if (byEmailIdx >= 0) {
                if (owner.id === companyOwnerId && resolveRole(directory[byEmailIdx].role) !== Role.SUPER_ADMIN) {
                    directory[byEmailIdx] = { ...directory[byEmailIdx], role: ownerRole };
                }
                continue;
            }
        }

        directory.unshift({
            ...(owner as User),
            role: ownerRole,
            companyId: owner.companyId ?? directory[0]?.companyId ?? null,
        });
    }

    return sortCompanyUserDirectory(
        dedupeCompanyUsersByEmail(directory, companyOwnerId),
        companyOwnerId,
    );
}

export function sortCompanyUserDirectory(users: User[], companyOwnerId?: string | null): User[] {
    return [...users].sort((a, b) => {
        const aIsPrimaryOwner = companyOwnerId && a.id === companyOwnerId ? 0 : 1;
        const bIsPrimaryOwner = companyOwnerId && b.id === companyOwnerId ? 0 : 1;
        if (aIsPrimaryOwner !== bIsPrimaryOwner) {
            return aIsPrimaryOwner - bIsPrimaryOwner;
        }

        const aIsElevated = resolveRole(a.role) === Role.SUPER_ADMIN ? 0 : 1;
        const bIsElevated = resolveRole(b.role) === Role.SUPER_ADMIN ? 0 : 1;
        if (aIsElevated !== bIsElevated) {
            return aIsElevated - bIsElevated;
        }

        const aName = (a.name || a.email || '').toLowerCase();
        const bName = (b.name || b.email || '').toLowerCase();
        return aName.localeCompare(bName);
    });
}

export async function loadCompanyUserDirectory(
    companyId: string,
    companyOwnerId?: string | null,
): Promise<User[]> {
    const [users, owners] = await Promise.all([
        fetchAllUsersForCompany(companyId),
        fetchCompanyOwnerRecords(companyId, companyOwnerId),
    ]);
    return mergeCompanyOwnersIntoUserDirectory(users, owners, companyOwnerId);
}

export type RemoveCompanyDirectoryMemberInput = {
    userId: string;
    companyId?: string | null;
    /** Company.companyOwnerId before delete */
    companyOwnerId?: string | null;
    /** Logged-in owner who should become companyOwnerId if the primary owner is removed */
    replacementOwnerId?: string | null;
};

export type RemoveCompanyDirectoryMemberResult = {
    deletedUser: boolean;
    deletedCompanyOwner: boolean;
    reassignedCompanyOwnerId: string | null;
};

/**
 * Removes a directory member for real.
 * Seeded/copied companies often keep a CompanyOwner row (e.g. Mark) even after
 * deleteUser — without deleting that owner, /users re-injects them on refresh.
 */
export async function removeCompanyDirectoryMember({
    userId,
    companyId,
    companyOwnerId,
    replacementOwnerId,
}: RemoveCompanyDirectoryMemberInput): Promise<RemoveCompanyDirectoryMemberResult> {
    let deletedUser = false;
    let deletedCompanyOwner = false;
    let reassignedCompanyOwnerId: string | null = null;

    try {
        await authClient.graphql({
            query: deleteUserMutation,
            variables: { input: { id: userId } },
        });
        deletedUser = true;
    } catch {
        // Owner-only directory rows have no User table record.
    }

   try {
       const ownerResult = await authClient.graphql({
           query: getCompanyOwner,
           variables: { id: userId },
       });
       if (ownerResult.data?.getCompanyOwner?.id === userId) {
           await authClient.graphql({
               query: deleteCompanyOwnerMutation,
               variables: { input: { id: userId } },
           });
           deletedCompanyOwner = true;
       }
   } catch {
       // Not every User has a CompanyOwner twin.
   }

    if (
        companyId &&
        companyOwnerId &&
        companyOwnerId === userId &&
        replacementOwnerId &&
        replacementOwnerId !== userId
    ) {
        await authClient.graphql({
            query: updateCompanyMutation,
            variables: {
                input: {
                    id: companyId,
                    companyOwnerId: replacementOwnerId,
                },
            },
        });
        reassignedCompanyOwnerId = replacementOwnerId;
    }

    if (!deletedUser && !deletedCompanyOwner) {
        throw new Error('Could not delete user or company owner record.');
    }

    return { deletedUser, deletedCompanyOwner, reassignedCompanyOwnerId };
}
